/**
 * Animation Context Provider
 * Provides global animation configuration and utilities throughout the app
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { AnimationConfig, PerformanceMetrics } from './config';
import { createOptimizedConfig, detectMotionPreferences } from './config';
import { AnimationPerformanceMonitor, getPerformanceMonitor } from './performance';

interface AnimationContextValue {
  config: AnimationConfig;
  updateConfig: (updates: Partial<AnimationConfig>) => void;
  performanceMetrics: PerformanceMetrics;
  isReducedMotion: boolean;
  performanceMonitor: AnimationPerformanceMonitor;
}

const AnimationContext = createContext<AnimationContextValue | null>(null);

interface AnimationProviderProps {
  children: ReactNode;
  initialConfig?: Partial<AnimationConfig>;
}

export const AnimationProvider: React.FC<AnimationProviderProps> = ({
  children,
  initialConfig = {},
}) => {
  const [config, setConfig] = useState<AnimationConfig>(() => ({
    ...createOptimizedConfig(),
    ...initialConfig,
  }));

  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    memoryUsage: 0,
    activeAnimations: 0,
    droppedFrames: 0,
  });

  const [isReducedMotion, setIsReducedMotion] = useState(detectMotionPreferences());
  const [performanceMonitor] = useState(() => getPerformanceMonitor(config));

  // Listen for changes in motion preferences
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsReducedMotion(e.matches);
      setConfig(prev => ({
        ...prev,
        reducedMotion: e.matches,
        enableComplexEffects: e.matches ? false : prev.enableComplexEffects,
      }));
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Start performance monitoring with enhanced features
  useEffect(() => {
    performanceMonitor.start();
    
    const unsubscribe = performanceMonitor.subscribe((metrics) => {
      setPerformanceMetrics(metrics);
      
      // Auto-adjust performance if needed
      if (!performanceMonitor.isPerformanceGood()) {
        const recommendations = performanceMonitor.getRecommendations();
        console.warn('Animation performance issues detected:', recommendations);
        
        // Auto-reduce performance level if FPS is consistently low
        if (metrics.fps < 45 && config.performance !== 'low') {
          console.log('Auto-reducing animation performance level');
          setConfig(prev => ({
            ...prev,
            performance: prev.performance === 'high' ? 'medium' : 'low',
            enableComplexEffects: prev.performance === 'high',
            maxConcurrentAnimations: prev.performance === 'high' ? 8 : 3,
          }));
        }
      }
    });

    // Setup budget violation monitoring
    const unsubscribeViolations = performanceMonitor.onBudgetViolation((violation, severity) => {
      if (severity === 'high') {
        console.error('Critical performance violation:', violation);
        
        // Force cleanup on critical violations
        performanceMonitor.forceCleanup();
      }
    });

    // Setup periodic cleanup
    const cleanupInterval = setInterval(() => {
      performanceMonitor.forceCleanup();
    }, 60000); // Every minute

    return () => {
      unsubscribe();
      unsubscribeViolations();
      clearInterval(cleanupInterval);
      performanceMonitor.stop();
      performanceMonitor.executeCleanup();
    };
  }, [performanceMonitor, config.performance]);

  const updateConfig = (updates: Partial<AnimationConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const contextValue: AnimationContextValue = {
    config,
    updateConfig,
    performanceMetrics,
    isReducedMotion,
    performanceMonitor,
  };

  return (
    <AnimationContext.Provider value={contextValue}>
      {children}
    </AnimationContext.Provider>
  );
};

/**
 * Hook to access animation context
 */
export const useAnimation = (): AnimationContextValue => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error('useAnimation must be used within an AnimationProvider');
  }
  return context;
};

/**
 * Hook to get animation configuration
 */
export const useAnimationConfig = (): AnimationConfig => {
  const { config } = useAnimation();
  return config;
};

/**
 * Hook to check if animations should be reduced
 */
export const useReducedMotion = (): boolean => {
  const { isReducedMotion } = useAnimation();
  return isReducedMotion;
};

/**
 * Hook to get performance metrics
 */
export const usePerformanceMetrics = (): PerformanceMetrics => {
  const { performanceMetrics } = useAnimation();
  return performanceMetrics;
};